import React, { ReactElement, useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { PatientDto } from '../../../dto/PatientDto';
import { Button, Grid, Segment } from 'semantic-ui-react';
import styles from './detailed-patient.module.css';
import DetailedPatientInfoField from './detailed-patient-info-field';
import PatientDiseases from './diseases/patient-diseases';
import { deletePatientFromDoctor, getPatientById } from '../../../api/routes/users';
import { deleteResult } from '../../../api/routes/results';
import { deleteDiseases } from '../../../api/routes/diseases';
import ConfirmDialog from '../../../components/confirm/confirm-dialog';
import { getSexByName } from '../../../utils/sex';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';

interface ParamsTypes {
  id: string;
}

export function DetailedPatient(): ReactElement {
  const { id } = useParams<ParamsTypes>();
  const [patient, setPatient] = useState<PatientDto | null>(null);
  const history = useHistory();
  const [isOpenDeletePatientDialog, setOpenDeletePatientDialog] = useState(false);
  const [deleteModal, setDeleteModal] = useState<ReactElement | null>(null);

  const fetchPatientAsync = async () => {
    const fetched = await getPatientById(id);
    setPatient(fetched);
  };

  const handleOnResultDelete = async (id: number) => {
    await deleteResult(id);
    await fetchPatientAsync();
  };

  const handleOnDiseasesDelete = async (id: number) => {
    await deleteDiseases(id.toString());
    await fetchPatientAsync();
  };

  const handleOnDeletePatient = async (patientId: number) => {
    await deletePatientFromDoctor(patientId);
    history.replace('/patients');
  };

  useEffect(() => {
    fetchPatientAsync();
  }, []);

  return (
    <>
      {patient && (
        <>
          <Grid>
            <Grid.Row centered>
              <Grid.Column width={11}>
                <h1>Карта пациента</h1>
              </Grid.Column>
              <Grid.Column width={3}>
                <Button
                  as={Link}
                  to={`/patients/detailed/${patient.userDto.id}/edit`}
                  className={styles.patientInfoMenuButton}
                >
                  Редактировать
                </Button>
              </Grid.Column>
              <Grid.Column width={2}>
                <Button
                  className={styles.patientInfoMenuButton}
                  primary
                  onClick={() => {
                    setOpenDeletePatientDialog(true);
                  }}
                >
                  Удалить
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Segment>
            <Grid className={styles.detailedPatientInfoGrid}>
              <DetailedPatientInfoField fieldName={'Фамилия'} value={patient.userDto.lastName} />
              <DetailedPatientInfoField fieldName={'Имя'} value={patient.userDto.firstName} />
              <DetailedPatientInfoField fieldName={'Отчество'} value={patient.userDto.patronymic} />
              <DetailedPatientInfoField fieldName={'Телефон'} value={patient.userDto.phone} />
              <DetailedPatientInfoField fieldName={'Почта'} value={patient.userDto.email} />
              <DetailedPatientInfoField fieldName={'Пол'} value={getSexByName(patient.userDto.sex).text} />
              <DetailedPatientInfoField
                fieldName={'День рождения'}
                value={format(parseISO(patient.userDto.birthday), 'd MMMM yyyy', { locale: ru})}
              />
              <Grid.Row>
                <Grid.Column>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={14}>
                        <h2>Болезни:</h2>
                      </Grid.Column>
                      <Grid.Column width={2}>
                        <Button primary as={Link} to={`/patients/detailed/${id}/add`}>
                          Добавить
                        </Button>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column>
                  <PatientDiseases
                    patientId={patient.userDto.id.toString()}
                    diseases={patient.diseases}
                    handleOnDiseasesDelete={(diseasesId) => {
                      setDeleteModal(
                        <ConfirmDialog
                          title={'Подтвердите действие'}
                          message={`Вы действительно хотите удалить болезнь ?`}
                          isOpen={true}
                          setOpen={() => setDeleteModal(null)}
                          onConfirm={() => {
                            handleOnDiseasesDelete(diseasesId);
                            setDeleteModal(null);
                          }}
                        />
                      );
                    }}
                    handleOnResultDelete={(resultId) => {
                      setDeleteModal(
                        <ConfirmDialog
                          title={'Подтвердите действие'}
                          message={`Вы действительно хотите удалить результат ?`}
                          isOpen={true}
                          setOpen={() => setDeleteModal(null)}
                          onConfirm={() => {
                            handleOnResultDelete(resultId);
                            setDeleteModal(null);
                          }}
                        />
                      );
                    }}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
          <ConfirmDialog
            title={'Подтвердите действие'}
            /* eslint-disable-next-line max-len */
            message={`Вы действительно хотите удалить пациента ${patient.userDto.lastName} ${patient.userDto.firstName} ${patient.userDto.patronymic} ?`}
            isOpen={isOpenDeletePatientDialog}
            setOpen={setOpenDeletePatientDialog}
            onConfirm={() => {
              handleOnDeletePatient(patient.userDto.id);
            }}
          />
          {deleteModal}
        </>
      )}
    </>
  );
}
